// composables/useClubContext.js
// Club-context layer — replaces the bare `isSupplier` boolean at call sites with
// a club-type aware API. The club type is *route-derived* (the `/suppliers/`
// namespace IS the context; there is no separate "active club" state machine),
// so a cold deep-link to `/suppliers/claims/` resolves to the suppliers club
// with zero extra state — exactly the property that made Option A the only
// cold-boot-safe shell architecture (see architecture doc Section 3).
//
// API:
//   clubType       — computed 'sellers' | 'suppliers' from the current route.
//   availableClubs — computed string[] of club types this user can enter.
//   switchClub(t)  — navigate to club t's shell root, dropping the back-stack.

import { f7 } from 'framework7-vue';
import { computed, unref } from 'vue';
import { CLUB_MANIFEST, clubTypeForPath, enumerateClubs } from '@/clubs/manifest.js';
import { useAuthStore } from '@/stores/authStore.js';

/**
 * @param {import('vue').Ref<object>|object} [currentRoute] - optional F7 current
 *   route (a ref, e.g. from `useShellRoute()`). Passing the reactive ref makes
 *   `clubType` update when the user switches shells. When omitted, clubType is
 *   read once from the live main-view router (sufficient for shell-stable reads,
 *   e.g. inside a guard, since the club only changes on a full shell switch).
 */
export function useClubContext(currentRoute) {
  const authStore = useAuthStore();

  function resolveUrl() {
    const route = unref(currentRoute);
    if (route?.url) return route.url;
    // Fallback: live router (non-reactive). f7 may be uninitialised in tests.
    return f7?.views?.main?.router?.currentRoute?.url ?? '/';
  }

  const clubType = computed(() => clubTypeForPath(resolveUrl()));

  const availableClubs = computed(() =>
    enumerateClubs({ supplierStage: authStore.supplierStage, isSupplier: authStore.isSupplier }),
  );

  /**
   * Switch to another club's shell. `clearPreviousHistory` is intentional: the
   * seller and supplier shells are different contexts, not parent/child, so the
   * back-stack from the previous shell is dropped (architecture doc Section 5.7).
   * No-op for an unknown club type.
   *
   * @param {string} type - a key of CLUB_MANIFEST ('sellers' | 'suppliers').
   */
  function switchClub(type) {
    const target = CLUB_MANIFEST[type]?.root;
    if (!target) return;
    const router = f7?.views?.main?.router;
    if (!router) return;
    router.navigate(target, { clearPreviousHistory: true });
  }

  return { clubType, availableClubs, switchClub };
}
